import InfoBadge from 'components/general/InfoBadge';
import LabeledSelect from 'components/general/inputs/LabeledSelect';
import LabeledDivider from 'components/general/LabeledDivider';
import StyledButton from 'components/general/StyledButton';
import { ILocationState, ISelectOption } from 'components/general/types';
import { IUser } from 'components/general/types/general';
import WaveContainer from 'components/general/WaveContainer';
import AuthWidget from 'components/general/widgets/AuthWidget';
import { GENERIC_ERROR_MESSAGE, SSO_REQUIRED } from 'config';
import { useFormikForm, useSnackbar, useUser } from 'hooks';
import { SatelliteApi } from 'middleware/SatelliteApi/api';
import microsoftLogo from 'multimedia/icons/microsoft.svg';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import Routes from 'routes';
import { TIdentityProvider, UserVables } from 'utils/vable';
import * as Yup from 'yup';
import useStyles from './styles';

interface IForm {
  identityProvider: ISelectOption | '';
}

const defaultValues: IForm = {
  identityProvider: '',
};

const loginSchema = Yup.object().shape({
  identityProvider: Yup.object().required('Select an identity provider.'),
});

const icons: Record<TIdentityProvider, string> = {
  [UserVables.IdentityProvider.Entra.value]: microsoftLogo,
};

/*
 * Handles the login process for SSO users. Allows them to select their identity provider,
 * then redirects them to the appropriate login page for that provider.
 */
const SsoLoginView = () => {
  const {
    User: {
      actions: { startExternalAuth },
    },
  } = SatelliteApi;
  const user = useUser();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const history = useHistory();
  const location = useLocation<ILocationState>();

  const { classes } = useStyles();

  const { from } = location.state || { from: { pathname: Routes.ROOT() } };

  useEffect(() => {
    // Redirect to ROOT if user is already authenticated
    if (user && !loading) {
      history.push(Routes.ROOT());
    }
  }, [history, user, loading]);

  const onSubmit = (values: IForm) => {
    setLoading(true);
    dispatch(
      startExternalAuth({
        queryParams: {
          identityProvider: (values.identityProvider as ISelectOption).value,
        },
        successCallback: (resp: { redirect: string }) => {
          window.location.href = resp.redirect;
        },
        failureCallback: () => {
          enqueueSnackbar(GENERIC_ERROR_MESSAGE, { variant: 'error' });
          setLoading(false);
        },
      })
    );
  };

  const { formik } = useFormikForm<IUser, IForm>(defaultValues, onSubmit, loginSchema);
  const { getFieldProps, handleSubmit } = formik;

  return (
    <WaveContainer>
      <AuthWidget>
        <form onSubmit={handleSubmit}>
          <LabeledSelect
            label={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Log in through your organization's identity provider (SSO):
                <InfoBadge content="Not seeing your organization's identity provider? Contact us to let us know what integrations you'd like to see!" />
              </span>
            }
            options={UserVables.IdentityProvider.options}
            getOptionLabel={(option: ISelectOption) => (
              <div className={classes.optionContainer}>
                <span>{option.label}</span>
                <img
                  className={classes.orgIcon}
                  src={icons[option.value as TIdentityProvider]}
                  alt={option.label}
                  height={18}
                />
              </div>
            )}
            {...getFieldProps('identityProvider')}
          />
          <StyledButton type="submit" disabled={loading} loading={loading} tallMargin fullWidth>
            Login
          </StyledButton>
          {!SSO_REQUIRED && (
            <>
              <LabeledDivider>or</LabeledDivider>
              <StyledButton
                type="button"
                disabled={loading}
                onClick={() => history.push({ pathname: Routes.LOGIN(), state: { from } })}
                framed
              >
                Log in through Sedaro
              </StyledButton>
            </>
          )}
        </form>
      </AuthWidget>
    </WaveContainer>
  );
};

export default withRouter(SsoLoginView);
export { default as SsoRedirect } from './SsoRedirect';
export { default as SsoSignup } from './SsoSignup';
